import { useCallback, useState } from 'react';
import { useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  PublishConflictReason,
  type PublishConflictError,
  type PublishDraftResponse,
  type PublishValidationError,
  type StepConflict
} from '@bliks-io/contracts';
import type { ApiError } from '@/lib/agent.types';
import { isPublishValidationError } from '@/api/errors';
import { toastApiError } from '@/api/mutations/toast-api-error';

/**
 * Owns the publish flow of a draft: flushing pending edits before the
 * request, handing a validation 400 to `onValidationRejected`, and the
 * unified live-conflict surface (load-time `liveConflict` + 409 response
 * conflicts share one render path).
 */
export function useDraftPublishHandlers(params: {
  onPublished: (result: PublishDraftResponse) => void;
  publishDraft: UseMutationResult<PublishDraftResponse, Error, { force?: boolean } | undefined>;
  loadTimeLiveConflict: StepConflict[] | null | undefined;
  onValidationRejected: (error: PublishValidationError) => void;
}) {
  const { onPublished, publishDraft, loadTimeLiveConflict, onValidationRejected } = params;
  const queryClient = useQueryClient();

  // Single source of truth for the live-process-changed dialog. Populated
  // either at load time (from the load response) or after a publish attempt
  // returns 409 `draft-stale`.
  const [publishConflict, setPublishConflict] = useState<StepConflict[] | null>(null);
  const [conflictDismissed, setConflictDismissed] = useState(false);
  // Covers the flush before `mutate` too, which `publishDraft.isPending` does not.
  const [isPublishPending, setIsPublishPending] = useState(false);

  // Two-layer defense against "publish reads a stale draft":
  //
  //  1. Blur the active input — field panels attach `flush` to onBlur, so
  //     blurring fires any pending debounced PATCH synchronously.
  //
  //  2. Wait for every in-flight mutation to settle before firing POST
  //     /publish. Even with (1), PATCH and POST hit the network at the same
  //     time — there's no HTTP-level guarantee PATCH arrives first, and the
  //     backend OCC only catches PATCH-landed-between-read-and-delete, not
  //     POST-beats-PATCH-to-the-server. Waiting client-side guarantees order.
  //
  // The backend OCC stays as belt-and-braces for cross-tab races.
  const flushAndWait = useCallback(async () => {
    if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    if (queryClient.isMutating() > 0) {
      await new Promise<void>((resolve) => {
        const cache = queryClient.getMutationCache();
        const timeout = setTimeout(() => {
          unsubscribe();
          resolve();
        }, 3000);
        const unsubscribe = cache.subscribe(() => {
          if (queryClient.isMutating() === 0) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }
  }, [queryClient]);

  const runPublish = useCallback(
    (variables: { force?: boolean } | undefined) => {
      setIsPublishPending(true);
      publishDraft.mutate(variables, {
        onSettled: () => setIsPublishPending(false),
        onSuccess: onPublished,
        onError: (error) => {
          if (isPublishValidationError(error)) {
            onValidationRejected({
              contentErrors: error.contentErrors ?? [],
              graphErrors: error.graphErrors ?? []
            });
            return;
          }
          const enriched = error as unknown as ApiError & Partial<PublishConflictError>;
          if (
            enriched.statusCode === 409 &&
            enriched.reason === PublishConflictReason.DRAFT_STALE
          ) {
            // Resets `conflictDismissed` so the dialog reopens even if the
            // user had previously dismissed the load-time warning.
            setPublishConflict(enriched.conflicts ?? []);
            setConflictDismissed(false);
            return;
          }
          // `draft-modified-during-publish` retries transparently in the
          // mutation layer; any other failure is unexpected.
          if (
            enriched.statusCode !== 409 ||
            enriched.reason !== PublishConflictReason.DRAFT_MODIFIED_DURING_PUBLISH
          ) {
            toastApiError(error, 'processDraft.publishUnknownError');
          }
        }
      });
    },
    [publishDraft, onPublished, onValidationRejected]
  );

  const handlePublish = useCallback(async () => {
    setIsPublishPending(true);
    await flushAndWait();
    runPublish(undefined);
  }, [flushAndWait, runPublish]);

  const handlePublishAnyway = useCallback(() => {
    setPublishConflict(null);
    setConflictDismissed(true);
    runPublish({ force: true });
  }, [runPublish]);

  const handleDismissConflict = useCallback(() => {
    setPublishConflict(null);
    setConflictDismissed(true);
  }, []);

  // Load-time conflict and publish-time conflict feed the same dialog. The
  // 409 payload takes precedence — it was re-detected against the freshest
  // live state. Once dismissed, the load-time signal stops re-firing the
  // dialog on every render. `conflictSource` lets the caller hide actions
  // that only make sense after a publish attempt (e.g. "Publish anyway" —
  // at load time the user hasn't asked to publish, so offering to force-
  // publish would be jarring).
  let activeConflicts: StepConflict[] | null = null;
  let conflictSource: 'publish' | 'load' | null = null;
  if (publishConflict !== null) {
    activeConflicts = publishConflict;
    conflictSource = 'publish';
  } else if (!conflictDismissed && loadTimeLiveConflict) {
    activeConflicts = loadTimeLiveConflict;
    conflictSource = 'load';
  }

  return {
    activeConflicts,
    conflictSource,
    handlePublish,
    handlePublishAnyway,
    handleDismissConflict,
    setConflictDismissed,
    isPublishPending
  };
}
